<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
    <nav v-if="isAuthenticated" class="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center space-x-3">
            <button
              @click="sidebarOpen = !sidebarOpen"
              class="md:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <X v-if="sidebarOpen" class="w-6 h-6" />
              <Menu v-else class="w-6 h-6" />
            </button>
            <router-link to="/dashboard" class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-spotify-green rounded-full flex items-center justify-center">
                <Music class="w-5 h-5 text-white" />
              </div>
              <span class="text-xl font-bold text-gray-900 dark:text-white">CD Shelfify prj.</span>
            </router-link>
          </div>

          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-3">
              <ThemeToggle />
              <img
                v-if="user?.profile_image_url"
                :src="user.profile_image_url"
                :alt="user.display_name"
                class="w-8 h-8 rounded-full"
              />
              <span class="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{{ user?.display_name }}</span>
              <button
                @click="logout"
                aria-label="Log out"
                class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <LogOut class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <div v-if="isAuthenticated" class="flex pt-16">
      <!-- Backdrop (mobile only) -->
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 z-40 bg-black/50 md:hidden"
        @click="sidebarOpen = false"
      ></div>

      <!-- Sidebar -->
      <aside
        :class="[
          'fixed top-16 left-0 h-[calc(100vh-4rem)] z-50 w-64 bg-white dark:bg-gray-800 shadow-sm border-r border-gray-200 dark:border-gray-700 transition-transform duration-300 overflow-y-auto',
          'md:translate-x-0 md:static md:h-auto md:min-h-[calc(100vh-4rem)]',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        ]"
      >
        <div class="p-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Library</h2>
          <nav class="space-y-2">
            <router-link
              to="/dashboard"
              class="sidebar-item"
              :class="{ active: $route.path === '/dashboard' }"
              @click="sidebarOpen = false"
            >
              <Home class="w-5 h-5 mr-3" />
              Dashboard
            </router-link>

            <router-link
              to="/folders"
              class="sidebar-item"
              :class="{ active: $route.path.startsWith('/folders') }"
              @click="sidebarOpen = false"
            >
              <Folder class="w-5 h-5 mr-3" />
              Folders
            </router-link>

            <router-link
              to="/vinyl-shelf"
              class="sidebar-item"
              :class="{ active: $route.path === '/vinyl-shelf' }"
              @click="sidebarOpen = false"
            >
              <Library class="w-5 h-5 mr-3" />
              Vinyl Shelf
            </router-link>

            <router-link
              to="/albums"
              class="sidebar-item"
              :class="{ active: $route.path.startsWith('/albums') }"
              @click="sidebarOpen = false"
            >
              <Disc class="w-5 h-5 mr-3" />
              Albums
            </router-link>

            <router-link
              to="/tags"
              class="sidebar-item"
              :class="{ active: $route.path.startsWith('/tags') }"
              @click="sidebarOpen = false"
            >
              <Tag class="w-5 h-5 mr-3" />
              Tags
            </router-link>

            <router-link
              to="/genres"
              class="sidebar-item"
              :class="{ active: $route.path.startsWith('/genres') }"
              @click="sidebarOpen = false"
            >
              <Hash class="w-5 h-5 mr-3" />
              Genres
            </router-link>

            <router-link
              to="/artists"
              class="sidebar-item"
              :class="{ active: $route.path.startsWith('/artists') }"
              @click="sidebarOpen = false"
            >
              <User class="w-5 h-5 mr-3" />
              Artists
            </router-link>

            <router-link
              to="/maintenance"
              class="sidebar-item"
              :class="{ active: $route.path.startsWith('/maintenance') }"
              @click="sidebarOpen = false"
            >
              <Settings class="w-5 h-5 mr-3" />
              Maintenance
            </router-link>
          </nav>
        </div>
      </aside>

      <!-- Main content -->
      <main class="flex-1 p-4 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-4rem)]">
        <router-view />
      </main>
    </div>

    <!-- Show router-view for unauthenticated users (landing page) -->
    <div v-else>
      <router-view />
    </div>

      <!-- Toast notifications -->
      <div class="fixed bottom-4 right-4 z-50 space-y-2 pointer-events-none">
        <transition-group name="toast">
          <div
            v-for="toast in toastStore.toasts"
            :key="toast.id"
            class="pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium max-w-sm"
            :class="{
              'bg-green-600 text-white': toast.type === 'success',
              'bg-red-600 text-white': toast.type === 'error',
              'bg-blue-600 text-white': toast.type === 'info',
            }"
          >
            <span>{{ toast.message }}</span>
            <button class="ml-auto opacity-75 hover:opacity-100" @click="toastStore.removeToast(toast.id)">✕</button>
          </div>
        </transition-group>
      </div>
      <CookieConsent />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import { Music, Home, Folder, Disc, Tag, Hash, User, LogOut, Settings, Menu, X, Library } from 'lucide-vue-next'
import ThemeToggle from '@/components/ThemeToggle.vue'
import CookieConsent from '@/components/CookieConsent.vue'
import { useThemeStore } from '@/stores/theme'

const toastStore = useToastStore()

const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()

const user = computed(() => authStore.user)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const sidebarOpen = ref(false)

const logout = () => {
  authStore.logout()
  router.push('/')
}

onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')

  if (token) {
    await authStore.setToken(token)
    router.push('/dashboard')
  }
})
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
